/**
 * Round 4 "Stores Management" — modale master per creare/modificare una sede
 * dentro un tenant. Stessa UX del modale sede tenant (`StoreModal`): sezioni
 * collassabili FormSection/FormField con icona colorata, stesso ordine e
 * titoli, senza le sezioni tenant-only (trunk SIP, orari, festività,
 * geofencing, social/tracking).
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Store as StoreIcon,
  Phone,
  Building2,
  Tag,
  MapPin,
  Plus,
  Trash2,
  Star,
  Loader2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-master/components/ui/dialog';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-master/components/ui/select';
import { SearchableSelect } from '@easyflow/ui-master/components/ui/searchable-select';
import { FormSection as Section, FormField as Field } from '@easyflow/ui-frontend';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import { toErr } from '@easyflow/shared';

type SedeType = 'punto_vendita' | 'ufficio';

interface TenantOption {
  id: string;
  name: string;
  slug: string;
}

interface CompanyOption {
  id: string;
  legal_name: string;
  vat_number: string | null;
}

interface CommercialAreaOption {
  id: string;
  code: string;
  display_name: string;
}

interface BrandChannelOption {
  id: string;
  brand_id: string;
  code: string;
  display_name: string;
  is_active: boolean;
  sort_order: number;
}

interface BrandOption {
  id: string;
  code: string;
  display_name: string;
  is_active: boolean;
  channels: BrandChannelOption[];
}

interface BrandAssociationDraft {
  brand_id: string;
  channel_id: string | null;
  retail_code: string;
  is_primary: boolean;
}

/** Sede in edit: shape del GET /master/stores/:id (subset usato dal form). */
export interface MasterStoreDetail {
  id: string;
  tenant_id: string;
  company_id?: string | null;
  name: string;
  sede_type?: SedeType;
  commercial_area_id?: string | null;
  address?: string | null;
  city?: string | null;
  postal_code?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  settings?: { timezone?: string } | null;
  brands?: Array<{
    brand_id: string;
    channel_id?: string | null;
    retail_code?: string | null;
    is_primary?: boolean;
  }>;
}

interface StoreFormModalMasterProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Se valorizzato → edit; altrimenti create. */
  store?: MasterStoreDetail | null;
  /** Preselezione tenant in create (facoltativa). */
  defaultTenantId?: string;
}

interface FormState {
  tenant_id: string;
  company_id: string;
  name: string;
  sede_type: SedeType;
  commercial_area_id: string | null;
  address: string;
  city: string;
  postal_code: string;
  country: string;
  phone: string;
  email: string;
  timezone: string;
  brands: BrandAssociationDraft[];
}

const emptyForm = (tenantId: string): FormState => ({
  tenant_id: tenantId,
  company_id: '',
  name: '',
  sede_type: 'punto_vendita',
  commercial_area_id: null,
  address: '',
  city: '',
  postal_code: '',
  country: 'Italy',
  phone: '',
  email: '',
  timezone: 'Europe/Rome',
  brands: [],
});

export function StoreFormModalMaster({
  isOpen,
  onClose,
  onSuccess,
  store,
  defaultTenantId,
}: StoreFormModalMasterProps) {
  const { toast } = useToast();
  const isEdit = Boolean(store?.id);

  const [isSaving, setIsSaving] = useState(false);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [areas, setAreas] = useState<CommercialAreaOption[]>([]);
  const [brandsCatalog, setBrandsCatalog] = useState<BrandOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm(defaultTenantId ?? ''));

  const loadTenants = useCallback(async () => {
    try {
      const res = await apiClient.get<TenantOption[]>('/master/stores/tenants');
      setTenants(res.data ?? []);
    } catch (err) {
      toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
    }
  }, [toast]);

  const loadBrandsCatalog = useCallback(async () => {
    try {
      const res = await apiClient.get<{ brands: BrandOption[] }>('/master/retail-brand-channels');
      setBrandsCatalog((res.data?.brands ?? []).filter((b) => b.is_active));
    } catch (err) {
      toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
    }
  }, [toast]);

  const loadFormOptions = useCallback(async (tenantId: string) => {
    if (!tenantId) {
      setCompanies([]);
      setAreas([]);
      return;
    }
    setLoadingOptions(true);
    try {
      const res = await apiClient.get<{
        companies: CompanyOption[];
        commercial_areas: CommercialAreaOption[];
      }>(`/master/stores/form-options/${tenantId}`);
      setCompanies(res.data?.companies ?? []);
      setAreas(res.data?.commercial_areas ?? []);
    } catch (err) {
      toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
      setCompanies([]);
      setAreas([]);
    } finally {
      setLoadingOptions(false);
    }
  }, [toast]);

  useEffect(() => {
    if (!isOpen) return;
    void loadBrandsCatalog();
    if (store) {
      setForm({
        tenant_id: store.tenant_id,
        company_id: store.company_id ?? '',
        name: store.name,
        sede_type: store.sede_type ?? 'punto_vendita',
        commercial_area_id: store.commercial_area_id ?? null,
        address: store.address ?? '',
        city: store.city ?? '',
        postal_code: store.postal_code ?? '',
        country: store.country ?? 'Italy',
        phone: store.phone ?? '',
        email: store.email ?? '',
        timezone: store.settings?.timezone ?? 'Europe/Rome',
        brands: (store.brands ?? []).map((b) => ({
          brand_id: b.brand_id,
          channel_id: b.channel_id ?? null,
          retail_code: b.retail_code ?? '',
          is_primary: Boolean(b.is_primary),
        })),
      });
      void loadFormOptions(store.tenant_id);
    } else {
      setForm(emptyForm(defaultTenantId ?? ''));
      void loadTenants();
      if (defaultTenantId) void loadFormOptions(defaultTenantId);
    }
    setBrandPickerOpen(false);
  }, [isOpen, store, defaultTenantId, loadBrandsCatalog, loadFormOptions, loadTenants]);

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]): void => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onTenantChange = (tenantId: string): void => {
    setForm((prev) => ({ ...prev, tenant_id: tenantId, company_id: '', commercial_area_id: null }));
    void loadFormOptions(tenantId);
  };

  const addBrand = (brandId: string): void => {
    setForm((prev) => {
      if (prev.brands.some((b) => b.brand_id === brandId)) return prev;
      return {
        ...prev,
        brands: [
          ...prev.brands,
          { brand_id: brandId, channel_id: null, retail_code: '', is_primary: prev.brands.length === 0 },
        ],
      };
    });
    setBrandPickerOpen(false);
  };

  const removeBrand = (brandId: string): void => {
    setForm((prev) => {
      const filtered = prev.brands.filter((b) => b.brand_id !== brandId);
      const first = filtered[0];
      if (first && !filtered.some((b) => b.is_primary)) {
        filtered[0] = { ...first, is_primary: true };
      }
      return { ...prev, brands: filtered };
    });
  };

  const setPrimaryBrand = (brandId: string): void => {
    setForm((prev) => ({
      ...prev,
      brands: prev.brands.map((b) => ({ ...b, is_primary: b.brand_id === brandId })),
    }));
  };

  const setBrandChannel = (brandId: string, channelId: string): void => {
    setForm((prev) => ({
      ...prev,
      brands: prev.brands.map((b) => (b.brand_id === brandId ? { ...b, channel_id: channelId } : b)),
    }));
  };

  const setBrandRetailCode = (brandId: string, code: string): void => {
    setForm((prev) => ({
      ...prev,
      brands: prev.brands.map((b) => (b.brand_id === brandId ? { ...b, retail_code: code } : b)),
    }));
  };

  const brandById = (id: string): BrandOption | undefined => brandsCatalog.find((b) => b.id === id);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!isEdit && !form.tenant_id) {
      toast({ title: 'Tenant obbligatorio', description: 'Seleziona il tenant di destinazione.', variant: 'destructive' });
      return;
    }
    if (!form.name.trim()) {
      toast({ title: 'Nome obbligatorio', description: 'Inserisci il nome della sede.', variant: 'destructive' });
      return;
    }
    if (!form.company_id) {
      toast({ title: 'Ragione sociale obbligatoria', description: 'Seleziona la ragione sociale emittente.', variant: 'destructive' });
      return;
    }
    if (form.brands.length === 0) {
      toast({ title: 'Insegna obbligatoria', description: 'Associa almeno un\'insegna alla sede.', variant: 'destructive' });
      return;
    }
    const missingChannel = form.brands.find((b) => !b.channel_id);
    if (missingChannel) {
      const label = brandById(missingChannel.brand_id)?.display_name ?? missingChannel.brand_id;
      toast({ title: 'Canale obbligatorio', description: `Seleziona il canale per l'insegna ${label}.`, variant: 'destructive' });
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        company_id: form.company_id,
        sede_type: form.sede_type,
        commercial_area_id: form.commercial_area_id,
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        postal_code: form.postal_code.trim() || null,
        country: form.country.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        settings: { timezone: form.timezone },
        brands: form.brands.map((b) => ({
          brand_id: b.brand_id,
          channel_id: b.channel_id,
          retail_code: b.retail_code.trim() !== '' ? b.retail_code.trim() : null,
          is_primary: b.is_primary,
        })),
      };

      if (isEdit && store) {
        await apiClient.put(`/master/stores/${store.id}`, payload);
        toast({ title: 'Sede aggiornata', description: 'La sede è stata aggiornata correttamente.' });
      } else {
        await apiClient.post('/master/stores', { ...payload, tenant_id: form.tenant_id });
        toast({ title: 'Sede creata', description: 'La sede è stata creata nel tenant selezionato.' });
      }
      onSuccess();
      onClose();
    } catch (err) {
      toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StoreIcon className="h-4 w-4 text-primary" />
            {isEdit ? `Modifica Sede — ${store?.name ?? ''}` : 'Nuova sede'}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            {isEdit
              ? 'Aggiorna i dati della sede nel tenant di appartenenza.'
              : 'Crea una sede dentro un tenant: ragione sociale e almeno un\'insegna con canale sono obbligatori.'}
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-3 mt-2">
            {/* Informazioni di base */}
            <Section title="Informazioni di base" icon={<Phone className="h-3.5 w-3.5" />} iconClassName="bg-blue-500/10 text-blue-600 dark:text-blue-400">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {!isEdit && (
                  <div className="sm:col-span-2">
                    <Field label="Tenant" required>
                      <SearchableSelect
                        value={form.tenant_id}
                        onValueChange={onTenantChange}
                        options={tenants.map((t) => ({ value: t.id, label: `${t.name} (${t.slug})` }))}
                        placeholder="Seleziona tenant..."
                        searchPlaceholder="Cerca tenant…"
                        emptyText="Nessun tenant trovato"
                      />
                    </Field>
                  </div>
                )}

                <Field label="Nome Sede" required>
                  <Input
                    id="ms-name"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    placeholder="Bologna Centrale"
                    required
                    className="h-9"
                  />
                </Field>
                <Field label="Ragione sociale" required>
                  <Select
                    value={form.company_id}
                    onValueChange={(v) => setField('company_id', v)}
                    disabled={!form.tenant_id || loadingOptions}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder={
                        !form.tenant_id
                          ? 'Seleziona prima il tenant'
                          : loadingOptions
                            ? 'Caricamento...'
                            : companies.length === 0
                              ? 'Nessuna ragione sociale nel tenant'
                              : 'Seleziona ragione sociale...'
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.legal_name}{c.vat_number ? ` — ${c.vat_number}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Telefono">
                  <Input
                    id="ms-phone"
                    value={form.phone}
                    onChange={(e) => setField('phone', e.target.value)}
                    placeholder="+39 051 123456"
                    className="h-9"
                  />
                </Field>
                <Field label="Email">
                  <Input
                    id="ms-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setField('email', e.target.value)}
                    placeholder="sede@example.com"
                    className="h-9"
                  />
                </Field>
                <Field label="Timezone">
                  <Select value={form.timezone} onValueChange={(v) => setField('timezone', v)}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Europe/Rome">Europe/Rome</SelectItem>
                      <SelectItem value="Europe/London">Europe/London</SelectItem>
                      <SelectItem value="Europe/Paris">Europe/Paris</SelectItem>
                      <SelectItem value="Europe/Madrid">Europe/Madrid</SelectItem>
                      <SelectItem value="Europe/Berlin">Europe/Berlin</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              {isEdit && store && (
                <Field label="ID interno (UUID)">
                  <Input value={store.id} disabled className="h-9" />
                  <p className="text-xs text-muted-foreground mt-1">UUID interno della sede (read-only).</p>
                </Field>
              )}
            </Section>

            {/* Tipologia & Area Commerciale */}
            <Section title="Tipologia & Area Commerciale" icon={<Building2 className="h-3.5 w-3.5" />} iconClassName="bg-indigo-500/10 text-indigo-600 dark:text-indigo-400" defaultOpen={false}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Tipologia" required>
                  <Select value={form.sede_type} onValueChange={(v: SedeType) => setField('sede_type', v)}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="punto_vendita">Punto Vendita</SelectItem>
                      <SelectItem value="ufficio">Ufficio</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Area Commerciale">
                  <Select
                    value={form.commercial_area_id ?? '__none__'}
                    onValueChange={(v) => setField('commercial_area_id', v === '__none__' ? null : v)}
                    disabled={!form.tenant_id || loadingOptions}
                  >
                    <SelectTrigger className="h-9"><SelectValue placeholder="Seleziona area..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Nessuna area</SelectItem>
                      {areas.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.display_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Aree visibili al tenant (config master).</p>
                </Field>
              </div>
            </Section>

            {/* Brand & Codici Retail */}
            <Section title="Brand & Codici Retail" icon={<Tag className="h-3.5 w-3.5" />} iconClassName="bg-amber-500/10 text-amber-600 dark:text-amber-400" defaultOpen={false}>
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Almeno un'insegna con canale. L'insegna <strong>primary</strong> (stella) determina il codice retail della sede.
                </p>

                {form.brands.length === 0 && (
                  <div className="text-sm text-muted-foreground py-3 text-center border border-dashed border-border/40 rounded-md">
                    Nessuna insegna associata. Aggiungi almeno un'insegna con canale.
                  </div>
                )}

                {form.brands.map((b) => {
                  const brand = brandById(b.brand_id);
                  const channels = (brand?.channels ?? []).filter((c) => c.is_active);
                  const channelMissing = !b.channel_id;
                  return (
                    <div key={b.brand_id} className="flex flex-wrap items-center gap-2 p-2 rounded-md border border-border/40 bg-background/40">
                      <Button
                        type="button"
                        variant={b.is_primary ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setPrimaryBrand(b.brand_id)}
                        title={b.is_primary ? 'Insegna primary' : 'Imposta come primary'}
                        className="shrink-0"
                      >
                        <Star className={b.is_primary ? 'h-4 w-4 fill-current' : 'h-4 w-4'} />
                      </Button>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{brand?.display_name ?? b.brand_id}</div>
                        <div className="text-xs text-muted-foreground">{brand?.code ?? ''}</div>
                      </div>
                      <div className="flex flex-col gap-1 w-48">
                        <Select
                          key={`channel-${b.brand_id}`}
                          value={b.channel_id ?? ''}
                          onValueChange={(v) => setBrandChannel(b.brand_id, v)}
                          disabled={channels.length === 0}
                        >
                          <SelectTrigger className={channelMissing ? 'h-9 border-destructive focus:ring-destructive' : 'h-9'}>
                            <SelectValue placeholder={channels.length === 0 ? 'Nessun canale' : 'Seleziona canale *'} />
                          </SelectTrigger>
                          <SelectContent>
                            {channels.map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {channelMissing && (
                          <span className="text-[11px] text-destructive">
                            {channels.length === 0 ? 'Nessun canale disponibile per questa insegna' : 'Canale obbligatorio'}
                          </span>
                        )}
                      </div>
                      <Input
                        value={b.retail_code}
                        onChange={(e) => setBrandRetailCode(b.brand_id, e.target.value)}
                        placeholder="Codice retail (es: BO-CENTRO-01)"
                        className="h-9 flex-1 max-w-xs"
                      />
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeBrand(b.brand_id)} title="Rimuovi insegna">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  );
                })}

                {brandPickerOpen ? (
                  <div className="flex gap-2 items-center">
                    <Select value="" onValueChange={addBrand}>
                      <SelectTrigger className="h-9 flex-1"><SelectValue placeholder="Seleziona insegna..." /></SelectTrigger>
                      <SelectContent>
                        {brandsCatalog
                          .filter((b) => !form.brands.some((x) => x.brand_id === b.id))
                          .map((b) => (
                            <SelectItem key={b.id} value={b.id}>{b.display_name} ({b.code})</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setBrandPickerOpen(false)}>
                      Annulla
                    </Button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" size="sm" onClick={() => setBrandPickerOpen(true)}>
                    <Plus className="h-4 w-4 mr-1" /> Aggiungi insegna
                  </Button>
                )}
              </div>
            </Section>

            {/* Indirizzo */}
            <Section title="Indirizzo" icon={<MapPin className="h-3.5 w-3.5" />} iconClassName="bg-teal-500/10 text-teal-600 dark:text-teal-400" defaultOpen={false}>
              <Field label="Indirizzo">
                <Input
                  id="ms-address"
                  value={form.address}
                  onChange={(e) => setField('address', e.target.value)}
                  placeholder="Via Roma 123"
                  className="h-9"
                />
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Città">
                  <Input
                    id="ms-city"
                    value={form.city}
                    onChange={(e) => setField('city', e.target.value)}
                    placeholder="Bologna"
                    className="h-9"
                  />
                </Field>
                <Field label="CAP">
                  <Input
                    id="ms-cap"
                    value={form.postal_code}
                    onChange={(e) => setField('postal_code', e.target.value)}
                    placeholder="40100"
                    className="h-9"
                  />
                </Field>
                <Field label="Paese">
                  <Input
                    id="ms-country"
                    value={form.country}
                    onChange={(e) => setField('country', e.target.value)}
                    placeholder="Italy"
                    className="h-9"
                  />
                </Field>
              </div>
            </Section>
          </div>

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
              Annulla
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEdit ? 'Salva modifiche' : 'Crea sede'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
